import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Save } from 'lucide-react'
import { settingsApi } from '../../services/api'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Card from '../../components/ui/Card'

const GROUP_ORDER = ['general', 'contact', 'admissions', 'social', 'recruitment']

const GROUP_LABEL_KEYS = {
  general: 'admin.settingsAdmin.general',
  contact: 'admin.settingsAdmin.contact',
  admissions: 'admin.settingsAdmin.admissions',
  social: 'admin.settingsAdmin.social',
  recruitment: 'admin.settingsAdmin.recruitment',
}

function formatSettingKey(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

function isMultilineSetting(item) {
  return item.key.includes('_text') || (item.value_en || '').length > 100
}

function getInputType(item) {
  if (item.type === 'email') return 'email'
  if (item.type === 'url') return 'url'
  if (item.type === 'phone') return 'tel'
  return 'text'
}

function SettingField({ item, onChange, t }) {
  const inputClass =
    'w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
  const multiline = isMultilineSetting(item)

  return (
    <div className="rounded-xl border border-border p-4">
      <div className="mb-3 text-sm font-medium text-primary">{formatSettingKey(item.key)}</div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs text-text/60">{t('admin.settingsAdmin.english')}</label>
          {multiline ? (
            <textarea
              rows={4}
              value={item.value_en || ''}
              onChange={(e) => onChange(item.id, 'value_en', e.target.value)}
              className={inputClass}
            />
          ) : (
            <input
              type={getInputType(item)}
              value={item.value_en || ''}
              onChange={(e) => onChange(item.id, 'value_en', e.target.value)}
              className={inputClass}
            />
          )}
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-text/60">{t('admin.settingsAdmin.arabic')}</label>
          {multiline ? (
            <textarea
              rows={4}
              value={item.value_ar || ''}
              onChange={(e) => onChange(item.id, 'value_ar', e.target.value)}
              dir="rtl"
              lang="ar"
              className={`font-ar ${inputClass}`}
            />
          ) : (
            <input
              type={getInputType(item)}
              value={item.value_ar || ''}
              onChange={(e) => onChange(item.id, 'value_ar', e.target.value)}
              dir="rtl"
              lang="ar"
              className={`font-ar ${inputClass}`}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default function AdminSettings() {
  const { t } = useTranslation()
  const [items, setItems] = useState([])
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    settingsApi
      .getAllAdmin()
      .then(({ data }) => {
        const list = data.data || data || []
        setItems(list)
        const firstGroup = GROUP_ORDER.find((group) => list.some((item) => (item.group || 'general') === group))
        setSelectedGroup(firstGroup || list[0]?.group || 'general')
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [])

  const grouped = useMemo(() => {
    return items.reduce((acc, item) => {
      const group = item.group || 'general'
      if (!acc[group]) acc[group] = []
      acc[group].push(item)
      return acc
    }, {})
  }, [items])

  const groups = useMemo(() => {
    const keys = Object.keys(grouped)
    return [
      ...GROUP_ORDER.filter((group) => keys.includes(group)),
      ...keys.filter((group) => !GROUP_ORDER.includes(group)).sort(),
    ]
  }, [grouped])

  const handleChange = (id, field, value) => {
    setSaved(false)
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSaved(false)
    try {
      await Promise.all(
        items.map((item) =>
          settingsApi.update(item.id, {
            key: item.key,
            value_en: item.value_en,
            value_ar: item.value_ar,
            type: item.type,
            group: item.group,
          }),
        ),
      )
      setSaved(true)
    } finally {
      setSaving(false)
    }
  }

  const getGroupLabel = (group) => {
    const key = GROUP_LABEL_KEYS[group]
    return key ? t(key) : formatSettingKey(group)
  }

  const activeItems = selectedGroup ? grouped[selectedGroup] || [] : []

  if (loading) return <LoadingSpinner className="py-20" />

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary">{t('admin.settingsAdmin.title')}</h1>
        <p className="mt-1 text-sm text-text/60">{t('admin.settingsAdmin.subtitle')}</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <Card padding={false} className="h-fit overflow-hidden lg:sticky lg:top-8">
          <div className="border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-primary">{t('admin.settingsAdmin.groupsList')}</h3>
          </div>
          <div className="max-h-[420px] overflow-y-auto lg:max-h-[calc(100vh-14rem)]">
            {groups.map((group) => {
              const isSelected = group === selectedGroup
              const count = grouped[group]?.length || 0
              return (
                <button
                  key={group}
                  type="button"
                  onClick={() => setSelectedGroup(group)}
                  className={`flex w-full items-center justify-between gap-2 border-b border-border/60 px-4 py-3 text-start transition-colors last:border-b-0 ${
                    isSelected ? 'bg-primary/5' : 'hover:bg-background'
                  }`}
                >
                  <span className={`truncate text-sm font-medium ${isSelected ? 'text-primary' : 'text-text'}`}>
                    {getGroupLabel(group)}
                  </span>
                  <span className="shrink-0 rounded-full bg-background px-2 py-0.5 text-[11px] text-text/50">
                    {count}
                  </span>
                </button>
              )
            })}
          </div>
        </Card>

        <Card padding={false} className="min-h-[420px] overflow-hidden lg:min-h-[calc(100vh-14rem)]">
          {selectedGroup && activeItems.length > 0 ? (
            <form onSubmit={handleSave} className="flex h-full flex-col">
              <div className="border-b border-border px-5 py-4">
                <h2 className="text-lg font-semibold text-primary">{getGroupLabel(selectedGroup)}</h2>
                <p className="mt-1 text-xs text-text/50">
                  {t('admin.settingsAdmin.settingsCount', { count: activeItems.length })}
                </p>
              </div>

              <div className="flex-1 space-y-4 overflow-y-auto p-5">
                {activeItems.map((item) => (
                  <SettingField key={item.id} item={item} onChange={handleChange} t={t} />
                ))}
              </div>

              <div className="flex items-center gap-4 border-t border-border bg-background/80 px-5 py-4">
                <Button type="submit" variant="primary" disabled={saving}>
                  <Save className="h-4 w-4" />
                  {saving ? t('common.loading') : t('buttons.save')}
                </Button>
                {saved && <span className="text-sm text-green-600">{t('admin.settingsAdmin.saved')}</span>}
              </div>
            </form>
          ) : (
            <div className="flex h-full min-h-[320px] items-center justify-center p-8 text-center text-sm text-text/50">
              {t('admin.settingsAdmin.selectGroup')}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
